import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Sparkle, BookOpen, ClockClockwise, Users, Brain, Compass, Feather, MagnifyingGlass, Lightbulb, ChatCircleText, Ticket, CheckCircle } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { CreativeWorkspace } from './creative/CreativeWorkspace';
import { PERSONA_STYLES } from './creative/personas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERSONA_ICONS = {
  novelist: Lightbulb,        // Maya · Innovator
  producer: Compass,          // Alex · Architect
  editor: Feather,            // Samantha · Wordsmith
  critic: MagnifyingGlass,    // Lawrence · Analyst
  researcher: BookOpen,       // Keith · Researcher
  mentor: Sparkle,            // Noah · Progress Coach
};

const FEATURES = [
  { icon: ClockClockwise, title: 'Persistent memory', body: 'Every character, plot idea, world-building note, business concept and deadline is remembered across sessions. Never start from scratch again.' },
  { icon: Sparkle, title: 'Idea → completion', body: 'From spark to polished submission. Brainstorm, plan, draft, critique, pitch — all with the same partner who knows your work.' },
  { icon: Users, title: '6 creative partners', body: 'Switch between Maya, Alex, Samantha, Lawrence, Keith and Noah depending on the stage of your project.' },
  { icon: ChatCircleText, title: 'Smart actions', body: 'One-tap prompts to brainstorm, challenge your idea, find plot holes, strengthen characters, and more.' },
];

export const CreativePartner = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const isAdmin = Boolean(user?.is_admin);
  const [status, setStatus] = useState(null);
  const [catalogue, setCatalogue] = useState(null);
  const [joining, setJoining] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [checkingPromo, setCheckingPromo] = useState(false);

  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) { toast.error('Enter a promo code'); return; }
    setCheckingPromo(true);
    try {
      const r = await axios.get(`${API}/promo/preview`, {
        params: { code, lookup_key: 'sub_creative_partner_yearly' },
        withCredentials: true,
      });
      setAppliedPromo(r.data);
      toast.success(`${r.data.trial_days}-day free trial applied`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid promo code');
    } finally {
      setCheckingPromo(false);
    }
  };

  const removePromo = () => { setAppliedPromo(null); setPromoInput(''); };

  useEffect(() => {
    axios.get(`${API}/creative-partner/personas`).then((r) => setCatalogue(r.data)).catch(() => setCatalogue(null));
  }, []);

  useEffect(() => {
    if (!isAuthenticated) { setStatus(null); return; }
    axios.get(`${API}/creative-partner/status`, { withCredentials: true })
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null));
  }, [isAuthenticated]);

  const handleJoin = async () => {
    if (!isAuthenticated) {
      navigate('/register?next=/creative-partner');
      return;
    }
    // Admins are complimentary — never hit Stripe, just refresh status and enter workspace
    if (isAdmin) {
      try {
        const r = await axios.get(`${API}/creative-partner/status`, { withCredentials: true });
        setStatus(r.data);
      } catch {/* keep going */}
      return;
    }
    setJoining(true);
    try {
      const payload = {
        lookup_key: 'sub_creative_partner_yearly',
        origin_url: window.location.origin,
      };
      if (appliedPromo?.code) payload.promo_code = appliedPromo.code;
      const r = await axios.post(
        `${API}/subscriptions/checkout`,
        payload,
        { withCredentials: true }
      );
      if (r.data?.admin_complimentary) {
        const s = await axios.get(`${API}/creative-partner/status`, { withCredentials: true });
        setStatus(s.data);
        return;
      }
      if (r.data?.url) window.location.href = r.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start checkout');
    } finally {
      setJoining(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="inline-block w-12 h-12 border-2 border-border border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  // Subscribed → workspace
  if (isAuthenticated && status?.active) {
    return <CreativeWorkspace catalogue={catalogue} status={status} onStatusChange={setStatus} />;
  }

  // Marketing page
  const personas = catalogue?.personas || [];

  return (
    <div className="min-h-screen bg-background text-ivory" data-testid="creative-partner-marketing">
      {/* Top nav */}
      <header className="h-16 border-b border-border flex items-center justify-between px-6 md:px-10">
        <div className="flex items-center gap-4">
          <Button onClick={() => navigate(isAuthenticated ? '/' : '/welcome')} className="rounded-lg font-medium text-xs uppercase tracking-[0.2em] px-4 py-2 bg-transparent text-ivory border border-border hover:border-gold hover:text-gold" data-testid="cp-back-btn">
            <ArrowLeft size={14} weight="duotone" className="mr-2" />
            {isAuthenticated ? 'Dashboard' : 'Home'}
          </Button>
          <h1 className="text-xl font-serif font-medium tracking-tight text-ivory">Manuscript Polish</h1>
        </div>
        {isAuthenticated && <span className="text-[11px] font-medium uppercase tracking-[0.28em] text-ash">{user?.email}</span>}
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16 text-center">
        <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold mb-5">Premium membership</p>
        <h1 className="text-5xl md:text-7xl font-serif font-light tracking-tight text-ivory leading-[1.05] mb-8">
          Creative <em className="text-gold not-italic font-normal">Partner</em>
        </h1>
        <p className="text-lg md:text-2xl font-serif font-light text-bone leading-[1.5] max-w-3xl mx-auto mb-10">
          A personalised creative partner that helps turn ideas into finished, publishable projects —
          and remembers every step of your journey.
        </p>
        <p className="text-base md:text-lg text-ash font-sans max-w-2xl mx-auto mb-12 leading-relaxed">
          Whether you&apos;re writing a novel, screenplay, TV format, song, business proposal, invention or a
          completely new idea, Creative Partner works alongside you from concept to completion.
          <br />
          <span className="text-gold font-medium">No idea is too big or too small.</span>
        </p>

        <div className="inline-flex flex-col items-center gap-4">
          {isAdmin ? (
            <div className="flex items-baseline gap-2">
              <span className="text-4xl md:text-5xl font-serif font-light text-emerald-300">Free</span>
              <span className="text-sm text-ash font-sans">· Host</span>
            </div>
          ) : (
            <div className="flex items-baseline gap-3">
              <span className="text-5xl md:text-6xl font-serif font-light text-ivory">£79</span>
              <span className="text-sm text-ash font-sans">/ year</span>
            </div>
          )}

          {/* Promo code (non-admin only) */}
          {!isAdmin && isAuthenticated && (
            <div className="w-full max-w-md">
              {appliedPromo ? (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-gold/40 bg-gold/5 px-4 py-3">
                  <div className="flex items-center gap-2 text-left">
                    <CheckCircle size={16} weight="duotone" className="text-emerald-400 shrink-0" />
                    <div>
                      <p className="text-sm font-serif text-ivory">
                        <span className="text-gold font-medium">{appliedPromo.code}</span> — first {appliedPromo.trial_days} days {appliedPromo.discount_percent === 100 ? 'free' : `${appliedPromo.discount_percent}% off`}
                      </p>
                      <p className="text-[10px] text-ash">Then auto-renews at £79/year</p>
                    </div>
                  </div>
                  <button
                    onClick={removePromo}
                    className="text-[10px] font-medium uppercase tracking-[0.24em] text-ash hover:text-rose-400"
                    data-testid="cp-remove-promo"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-lg border border-border bg-stone/40 px-3 py-2">
                  <Ticket size={14} weight="duotone" className="text-gold shrink-0" />
                  <input
                    type="text"
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                    placeholder="Promo code (optional)"
                    className="flex-1 bg-transparent text-ivory font-mono text-sm py-1 focus:outline-none"
                    data-testid="cp-promo-input"
                  />
                  <button
                    type="button"
                    onClick={applyPromo}
                    disabled={checkingPromo || !promoInput.trim()}
                    className="text-[10px] font-medium uppercase tracking-[0.24em] text-gold hover:text-gold-dark disabled:opacity-50"
                    data-testid="cp-apply-promo"
                  >
                    {checkingPromo ? 'Checking…' : 'Apply'}
                  </button>
                </div>
              )}
            </div>
          )}

          <Button
            onClick={handleJoin}
            disabled={joining}
            className="rounded-lg font-medium text-sm tracking-wide px-10 py-4 bg-gold text-charcoal hover:bg-gold-dark disabled:opacity-50 border border-transparent inline-flex items-center gap-2"
            data-testid="cp-join-btn"
          >
            {joining ? 'Opening checkout…' : (isAdmin ? 'Enter your studio' : (appliedPromo ? `Start ${appliedPromo.trial_days}-day free trial` : (isAuthenticated ? 'Join Creative Partner' : 'Sign up & join')))}
            <ArrowRight size={16} weight="bold" />
          </Button>
          <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash mt-1">
            {isAdmin ? 'Complimentary for the host account' : '365 days of access · renewable · cancel anytime'}
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-border/60 bg-card/30">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="panel-luxe p-8 md:p-10">
                  <Icon size={26} weight="duotone" className="text-gold mb-5" />
                  <h3 className="text-2xl font-serif font-light text-ivory mb-3">{f.title}</h3>
                  <p className="text-base text-bone font-sans leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Personas */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="text-center mb-14">
          <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold mb-3">Choose your creative partner</p>
          <h2 className="text-4xl md:text-5xl font-serif font-light tracking-tight text-ivory">Seven partners. One workspace.</h2>
          <p className="text-base text-bone font-sans max-w-2xl mx-auto mt-5 leading-relaxed">
            Switch between them at any time, depending on the stage of your project.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {personas.map((p) => {
            const styles = PERSONA_STYLES[p.id] || { accent: 'text-gold', ring: 'border-gold/40' };
            const Icon = PERSONA_ICONS[p.id] || Sparkle;
            return (
              <article key={p.id} className={`panel-luxe p-7 border ${styles.ring}`} data-testid={`persona-card-${p.id}`}>
                <Icon size={22} weight="duotone" className={`${styles.accent} mb-4`} />
                <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash mb-1">{p.title}</p>
                <h3 className={`text-2xl font-serif font-normal ${styles.accent} mb-3`}>{p.display_name}</h3>
                <p className="text-sm text-bone font-sans leading-relaxed">{p.tagline}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-t border-border bg-card/40">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-20 text-center">
          <h2 className="text-4xl md:text-5xl font-serif font-light tracking-tight text-ivory mb-6 leading-[1.1]">
            Your creative studio, <em className="text-gold not-italic font-normal">on-call</em>.
          </h2>
          <p className="text-base md:text-lg text-bone font-sans max-w-xl mx-auto mb-10 leading-relaxed">
            {isAdmin
              ? 'Persistent memory. Seven expert partners. Every stage from idea to finished pitch — complimentary for the host account.'
              : 'Persistent memory. Seven expert partners. Every stage from idea to finished pitch — £79 a year.'}
          </p>
          <Button
            onClick={handleJoin}
            disabled={joining}
            className="rounded-lg font-medium text-sm tracking-wide px-10 py-4 bg-gold text-charcoal hover:bg-gold-dark disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="cp-join-btn-bottom"
          >
            {joining ? 'Opening checkout…' : 'Join Creative Partner'}
            <ArrowRight size={16} weight="bold" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 flex items-center justify-between text-xs text-ash font-sans">
          <span>© {new Date().getFullYear()} Manuscript Polish</span>
          <div className="flex gap-5">
            <Link to="/how-to" className="hover:text-gold transition-colors">How it works</Link>
            <Link to="/about" className="hover:text-gold transition-colors">About</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};
